import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useEffect, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'

export default function ACHAgreementPage() {
  const navigate = useNavigate()
  const { applicationId } = useParams()
  const [searchParams] = useSearchParams()
  const isRefundIssued = !!searchParams.get('refundIssued')
  const isUpdate = !!searchParams.get('update')
  const isSpecialFlow = isUpdate || isRefundIssued

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [signingUrl, setSigningUrl] = useState('')

  useEffect(() => { createSession() }, [])

  // Listen for ESIGN_COMPLETE from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'esign_complete' || event.data?.type === 'ESIGN_COMPLETE') {
        handleFinishACHAgreement()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const createSession = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${apiURL}/api/applications/${applicationId}/ach-agreement/`, {
        headers: { 'Authorization': account.accessToken }
      })
      const data = await response.json()

      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.' || data.response.error === 'No Authorization Token Provided.') {
          const refreshed = await refreshAccessToken()
          if (refreshed && refreshed.length > 0) return createSession()
          return navigate('/', { state: { expired: true } })
        }
        setError(data.response.error)
        return
      }

      if (data.response.status === 'completed' || data.response.status === 'complete') {
        handleFinishACHAgreement()
        return
      }

      setSigningUrl(data.response.signing_url)
    } catch (err) {
      setError('Failed to load the ACH Agreement. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFinishACHAgreement = async () => {
    try {
      const response = await fetch(`${apiURL}/api/applications/${applicationId}/ach-agreement/`, {
        method: 'PUT',
        headers: {
          'Authorization': account.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refundIssued: isRefundIssued, update: isUpdate })
      })
      const data = await response.json()

      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.' || data.response.error === 'No Authorization Token Provided.') {
          const refreshed = await refreshAccessToken()
          if (refreshed && refreshed.length > 0) return handleFinishACHAgreement()
          return navigate('/', { state: { expired: true } })
        }
        console.error(data.response.error)
        return
      }

      navigate(
        isSpecialFlow
          ? `/applications/${applicationId}/dashboard`
          : `/applications/${applicationId}/e-sign`
      )
    } catch (err) {
      console.error('Error completing ACH Agreement:', err)
    }
  }

  return (
    <div className='w-full h-full flex flex-col items-center justify-center bg-white dark:bg-slate-900 p-6 overflow-hidden transition-colors duration-300'>
      <div className='w-full h-full flex flex-col p-8 min-h-0'>
        <div className='mb-8 shrink-0 transition-colors duration-300'>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white text-center mb-3'>ACH Agreement</h1>
          <p className='text-gray-600 dark:text-slate-400 text-center'>Please make sure to read and e-sign the following ACH Agreement.</p>
        </div>
        <div className='w-full flex-1 min-h-0'>
          {loading ? (
            <div className='flex flex-row items-center justify-center gap-2 py-8 transition-colors duration-300'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
              <p className='text-slate-600 dark:text-slate-300'>Loading...</p>
            </div>
          ) : error ? (
            <div className='flex flex-col items-center justify-center gap-4 p-8 transition-colors duration-300'>
              <div className='bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 max-w-md text-center'>
                <p className='text-red-600 dark:text-red-400 font-medium mb-4'>{error}</p>
                <button
                  onClick={() => { setError(''); createSession() }}
                  className='px-6 py-2 bg-ertc-primary text-white rounded-lg hover:bg-ertc-primary-dark transition-all cursor-pointer'
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className='w-full h-full flex flex-col items-center bg-gray-50 dark:bg-slate-800 rounded-lg overflow-hidden'>
              <iframe
                src={signingUrl}
                className='w-full h-full border-0'
                title='E-Sign ACH Agreement'
                allow='fullscreen'
              />
              <div className='p-4 text-center shrink-0'>
                <p className='text-sm text-gray-500 dark:text-slate-400'>
                  Already signed this document?{' '}
                  <button
                    onClick={handleFinishACHAgreement}
                    className='text-ertc-primary hover:text-ertc-primary-dark font-semibold underline cursor-pointer'
                  >
                    Click here to continue
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
